import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/AddUnit.css"; // Assuming the same CSS can be used

const AddDepartment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [departmentName, setDepartmentName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [departmentId, setDepartmentId] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    // Check if we're in edit mode
    if (location.state && location.state.department && location.state.isEditing) {
      const { department } = location.state;
      setDepartmentName(department.dept_name);
      setDepartmentId(department.id);
      setIsEditing(true);
    } else {
      // Reset to add mode if no edit state
      setDepartmentName("");
      setDepartmentId(null);
      setIsEditing(false);
    }
  }, [location.state]);

  // Cleanup effect to reset state when component unmounts or location changes
  useEffect(() => {
    return () => {
      setDepartmentName("");
      setDepartmentId(null);
      setIsEditing(false);
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!departmentName.trim()) {
      showToast("Please enter a department name", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = isEditing ? "edit-department.php" : "add-department.php";
      const body = isEditing 
        ? { id: departmentId, dept_name: departmentName }
        : { dept_name: departmentName };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.message && data.message.includes("successfully")) {
        const action = isEditing ? "updated" : "added";
        showToast(
          `Department "${departmentName}" ${action} successfully!`,
          "success"
        );
        setDepartmentName("");
        setTimeout(() => {
          navigate("/department-list");
        }, 1500);
      } else if (data.status === "success") {
        // Fallback for APIs that use status field
        const action = isEditing ? "updated" : "added";
        showToast(
          `Department "${departmentName}" ${action} successfully!`,
          "success"
        );
        setDepartmentName("");
        setTimeout(() => {
          navigate("/department-list");
        }, 1500);
      } else {
        showToast(
          data.message || `Failed to ${isEditing ? 'update' : 'add'} department`,
          "error"
        );
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // You might want to rename this class to 'add-department-content'
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>{isEditing ? "Edit Department" : "Add Department"}</h1>
        <p>{isEditing ? "Update department information" : "Create a new department for your organization"}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="departmentName">Department Name</label>
            <input
              type="text"
              id="departmentName"
              name="departmentName"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              placeholder="Enter department name (e.g., Civil, Mechanical)"
              required
              disabled={isSubmitting}
            />
          </div>
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting 
              ? (isEditing ? "Updating Department..." : "Adding Department...") 
              : (isEditing ? "Update Department" : "Add Department")
            }
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDepartment;
